import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from '@/components'
import {
  PageContainer,
  DetailHeader,
  Section,
  List,
  ListRow,
  MetaText,
} from '@/components/layout'
import { useAuthStore, toast } from '@/store'

interface SettingItem {
  icon: string
  label: string
  path?: string
  value?: string
  onClick?: () => void
}

interface SettingSection {
  /** Optional section heading rendered above the list. */
  title?: string
  items: SettingItem[]
}

export function SettingsPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { logout } = useAuthStore()

  const settingSections: SettingSection[] = [
    {
      items: [
        { icon: 'manage_accounts', label: t('settings.accountSecurity'), path: '/settings/account' },
        { icon: 'settings',        label: t('settings.general'),         path: '/settings/general' },
        { icon: 'notifications',   label: t('settings.notifications'),   path: '/settings/notifications' },
        { icon: 'translate',       label: t('settings.language'),        path: '/settings/language' },
        { icon: 'lock',            label: t('settings.privacy'),         path: '/settings/privacy' },
      ],
    },
    {
      items: [
        { icon: 'tune',       label: t('settings.feedPreferences'), path: '/settings/content' },
        { icon: 'psychology', label: t('settings.memory'),          path: '/settings/memory' },
        { icon: 'science',    label: t('settings.betaFeatures'),    path: '/settings/beta' },
      ],
    },
    {
      items: [
        { icon: 'help_outline', label: t('settings.helpCenter'), path: '/help' },
        { icon: 'info',         label: t('settings.aboutAlive'), path: '/settings/about' },
      ],
    },
  ]

  const handleItemClick = (item: SettingItem) => {
    if (item.onClick) {
      item.onClick()
    } else if (item.path) {
      navigate(item.path)
    }
  }

  const handleLogout = () => {
    logout()
    toast.success(t('settings.loggedOut', 'Logged out'))
    navigate('/auth/login')
  }

  return (
    <PageContainer
      width="list"
      showTabBar={false}
      header={<DetailHeader title={t('settings.title')} />}
    >
      {settingSections.map((section, sectionIndex) => (
        <Section key={sectionIndex} title={section.title}>
          <List>
            {section.items.map((item) => (
              <ListRow
                key={item.icon}
                avatar={
                  <div className="w-9 h-9 rounded flex items-center justify-center bg-primary/10 text-primary">
                    <Icon name={item.icon} size={20} />
                  </div>
                }
                title={item.label}
                meta={item.value ? <MetaText>{item.value}</MetaText> : undefined}
                chevron
                onClick={() => handleItemClick(item)}
              />
            ))}
          </List>
        </Section>
      ))}

      {/* Logout — destructive action button at the bottom of the
          page, separated from the settings groups by margin. */}
      <div className="px-4 mt-8 mb-6">
        <button
          type="button"
          onClick={handleLogout}
          className="
            w-full py-3 rounded
            bg-gray-50 dark:bg-white/[0.03]
            text-red-500 text-sm font-medium
            hover:bg-gray-100 dark:hover:bg-white/[0.06]
            transition-colors
          "
        >
          {t('settings.logOut')}
        </button>
      </div>
    </PageContainer>
  )
}
